import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { lightThemeColors } from "../../assets/styles/colors";
import PrimaryButton from "../../Components/Generic/PrimaryButton";
import { ReactComponent as CopyIcon } from "../../assets/icons/copyIcon.svg";
import { ReactComponent as DotMenuIcon } from "../../assets/icons/dotMenuIcon.svg";
import SecondaryButton from "../../Components/Generic/SecondaryButton";
import { AnimatePresence } from "framer-motion";
import InviteMembersModal from "../../Components/MembersPage/InviteMembersModal";
import { useMembersContext } from "../../context/useMembersContext";
import { useProjectContext } from "../../context/useProjectContext";
import { useOutletContext, useSearchParams } from "react-router-dom";
import {
  showGenericError,
  showSuccessNotification,
} from "../../helper/Notifications";
import DatePickerWithOptions from "../../Components/Generic/DatePickerWithOptions";
import moment from "moment";
import DeleteConfirmationChild from "../../Components/Generic/DeleteConfirmationWithChild";
import { DELETE_PROJECT } from "../../constants/OptionsConstants";
import { lastIndexOf } from "lodash";
import DocumentationPage from "../DocumentationPage/DocumentationPage";
import MembersPage from "../MembersPage/MembersPage";
import PageHeader from "../../Components/Layout/PageHeader";
import TabNav from "../../Components/Generic/TabNav";
import { Users, AlertTriangle, Trash2 } from "lucide-react";

const TAB_ACCESS = "access";
const TAB_DANGER = "danger";

const PROJECT_TABS = [
  { key: TAB_ACCESS, label: "Access Management", icon: Users },
  { key: TAB_DANGER, label: "Danger Zone", icon: AlertTriangle },
];

const ProjectPage = () => {
  const {
    selectedProject,
    getProjectUsage,
    deleteProject,
    getProjects,
    setSelectedProject,
    isAdmin,
  } = useProjectContext();
  const { memberCounts } = useMembersContext();
  const { setLoading } = useOutletContext();

  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] =
    useState(false);
  const [showInviteMembers, setShowInviteMembers] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_ACCESS);

  const { setCreateFirstProjectModalOpen } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleGetProjects = () => {
    setLoading(true);
    getProjects(
      (response) => {
        handleProjectResponse(response.data.response);
        setLoading(false);
      },
      (error) => {
        setLoading(false);
        showGenericError();
        console.log(error);
      }
    );
  };

  const handleProjectResponse = (projects) => {
    if (projects.length > 0) {
      let projectToUse = projects[0];
      try {
        const savedId = localStorage.getItem("selectedProjectId");
        if (savedId) {
          const restored = projects.find((p) => String(p.id) === String(savedId));
          if (restored) projectToUse = restored;
        }
      } catch (e) {}
      setSearchParams((params) => {
        params.set("project_id", String(projectToUse.id));
        return params;
      });
      setSelectedProject(projectToUse);
      setLoading(false);
    } else {
      setCreateFirstProjectModalOpen(true);
    }
  };

  const handleDeleteProject = () => {
    deleteProject(
      selectedProject.id,
      (response) => {
        setSearchParams((prev) => {
          prev.delete("project_id");
        });
        handleGetProjects();
      },
      (error) => {
        console.log(error);
        showGenericError();
      }
    );
  };


  const totalMembers = memberCounts?.total ?? 0;
  const adminCount = memberCounts?.admin ?? 0;
  const memberCount = memberCounts?.member ?? 0;

  return (
    <Container>
      <PageContent>
        <Header>
          <HeaderContent>
            <PageHeader
              title="Settings"
              subtitle="Manage your workspace: add admins, invite developers (API key access only), or delete the project."
            />
          </HeaderContent>
          <StatRow>
            <StatChip>
              <StatValue>{totalMembers}</StatValue>
              <StatLabel>Total Users</StatLabel>
            </StatChip>
            <StatChip>
              <StatValue>{adminCount}</StatValue>
              <StatLabel>Admin</StatLabel>
            </StatChip>
            <StatChip>
              <StatValue>{memberCount}</StatValue>
              <StatLabel>Member</StatLabel>
            </StatChip>
          </StatRow>
        </Header>
        <ContentWrapper>
          {isAdmin && (
            <TabNav tabs={PROJECT_TABS} activeKey={activeTab} onChange={setActiveTab} />
          )}

          {(activeTab === TAB_ACCESS || !isAdmin) && (
            <AccessSection>
              <SectionHeader>
                <SectionTitle>Access Management</SectionTitle>
              </SectionHeader>
              <Line>
                <MembersPage embedded={true} showInviteMembers={showInviteMembers} setShowInviteMembers={setShowInviteMembers} />
              </Line>
            </AccessSection>
          )}

          {activeTab === TAB_DANGER && isAdmin && (
            <DangerCard>
              <DangerHeader>
                <AlertTriangle size={16} strokeWidth={2} />
                Danger Zone
              </DangerHeader>
              <DangerRow>
                <DangerRowText>
                  <DangerZoneTitle>Delete this subscription</DangerZoneTitle>
                  <DangerZoneDescription>
                    Erases all data and settings - {totalMembers} member{totalMembers === 1 ? "" : "s"} will immediately lose access. This cannot be undone.
                  </DangerZoneDescription>
                </DangerRowText>
                <DeleteConfirmationChild
                  open={deleteConfirmationModalOpen}
                  setOpen={setDeleteConfirmationModalOpen}
                  item={selectedProject}
                  action={handleDeleteProject}
                  variant={DELETE_PROJECT}
                  >
                  <SecondaryButton
                    icon={<Trash2 size={16} strokeWidth={2} />}
                    onClick={() => setDeleteConfirmationModalOpen(true)}
                    variant="error"
                    text={"Delete Subscription"}
                    />
                </DeleteConfirmationChild>
              </DangerRow>
            </DangerCard>
          )}
        </ContentWrapper>
      </PageContent>
    </Container>
  );
};

export default ProjectPage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  overflow-y: auto;
  padding-bottom: 30px;
  padding: 40px 30px;
  position: relative;
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
`;

const StatRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const StatChip = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 16px;
  border: 1px solid #e9eaeb;
  background: #fff;
  border-radius: 10px;
  min-width: 96px;
`;

const StatValue = styled.span`
  font-size: 18px;
  font-weight: 800;
  color: ${lightThemeColors.darkColor};
`;

const StatLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #98a2b3;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const HeaderTitle = styled.h1`
  color: ${lightThemeColors.darkColor};
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  text-align: left;
  margin: 0;
`;

const HeaderSubtitle = styled.p`
  color: ${lightThemeColors.darkColor50};
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  margin: 0;
  max-width: 800px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
`;

const SectionTitle = styled.h2`
  color: ${lightThemeColors.darkColor};
  font-size: 24px;
  font-weight: 600;
  line-height: 36px;
  margin: 0;
`;
const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
  width: 100%;
`;

const Line = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const ListHeader = styled(Line)`
  background: ${lightThemeColors.menuBackground};
  padding: 13px 20px;
  gap: 50px;
  width: 100%;
  min-width: 750px;
  min-height: 50px;
`;

const HeaderColumn = styled.div`
  display: flex;
  width: 100%;
  min-width: 125px;
  align-items: center;

  p {
    color: ${lightThemeColors.darkColor50};
    font-size: 16px;
    font-weight: 600;
    line-height: 19px;
    text-align: left;
  }
`;

const ListLine = styled(Line)`
  border-bottom: 1px solid ${lightThemeColors.menuBackground};
  padding: 13px 20px;
  gap: 50px;
  width: 100%;
  min-width: 750px;
  min-height: 50px;
`;
const ListColumn = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 125px;
  h3 {
    text-overflow: ellipsis;
    overflow: hidden;
    color: ${lightThemeColors.darkColor};
    font-size: 16px;
    font-weight: 400;
    line-height: 19px;
  }

  h2 {
    text-overflow: ellipsis;
    overflow: hidden;
    color: ${lightThemeColors.darkColor};
    font-size: 18px;
    font-weight: 600;
    line-height: 22px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const AccessSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Subtitle = styled.h2`
  color: ${lightThemeColors.darkColor};
  font-size: 24px;
  font-weight: 600;
  line-height: 30px;
  text-align: left;
`;

const DangerCard = styled.div`
  border: 1px solid #fecdca;
  border-radius: 12px;
  overflow: hidden;
  max-width: 720px;
`;

const DangerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  background: #fef3f2;
  border-bottom: 1px solid #fecdca;
  color: #b42318;
  font-size: 13px;
  font-weight: 800;
`;

const DangerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 16px 18px;
  background: #fff;
  flex-wrap: wrap;
`;

const DangerRowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const DangerZoneTitle = styled.h2`
  color: ${lightThemeColors.darkColor};
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
  text-align: left;
  margin: 0;
`;

const DangerZoneDescription = styled.p`
  color: ${lightThemeColors.darkColor50};
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
  max-width: 460px;
`;
